/**
 * ReferralsPage — "¡Invita y gana!" del portal.
 *
 * Conectado a /sellers (backend real).
 * Si el usuario no es seller registrado, mostramos EmptyState con CTA a soporte.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import { StatusBadge } from '@app/components/common/StatusBadge';
import {
  useReferralLink,
  useReferrals,
  useSellerMe,
} from '@modules/sellers/presentation/hooks/useSellers';
import { formatDate } from '@shared/utils/formatters';
import { toast } from 'sonner';
import {
  Share2,
  Copy,
  Mail,
  MessageCircle,
  Headphones,
  Gift,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@shared/utils/cn';

export function ReferralsPage() {
  const { data: link, isLoading: loadingLink, error: linkError } = useReferralLink();
  const { data: referrals = [], isLoading: loadingRefs } = useReferrals();
  const { data: seller } = useSellerMe();
  const [emailInput, setEmailInput] = useState('');

  const fullLink = link?.referral_link ?? '';
  const code = link?.referral_code ?? '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullLink);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No pudimos copiar. Copiá manualmente.');
    }
  };

  const handleWhatsApp = () => {
    const text = `¡Conoce mishiwoof! Asistencia para mascotas con beneficios exclusivos. Te invito a registrarte con mi enlace: ${fullLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const handleEmailInvite = () => {
    if (!emailInput.trim()) {
      toast.error('Ingresa un email');
      return;
    }
    const subject = encodeURIComponent('Te invito a mishiwoof');
    const body = encodeURIComponent(
      `Hola!\n\nQuiero invitarte a mishiwoof, la plataforma de asistencia para mascotas que estoy usando.\nRegístrate con mi enlace: ${fullLink}\n\nUsa el código: ${code}`,
    );
    window.location.href = `mailto:${emailInput.trim()}?subject=${subject}&body=${body}`;
    setEmailInput('');
  };

  if (linkError) {
    return (
      <div className="space-y-6">
        <PageHeader title="¡Invita y gana!" subtitle="Invita amigos y gana cuotas gratis" />
        <EmptyState
          icon={Share2}
          title="No tienes programa de referidos activo"
          description="El programa de referidos es para socios activos. Si crees que es un error, escríbenos."
          action={
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/chat">Contactar a soporte</Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="¡Invita amigos y gana cuotas gratis!"
        subtitle="Comparte tu enlace y obtén beneficios por cada amigo que se registre"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Columna izquierda (más ancha) */}
        <div className="space-y-6 lg:col-span-2">
          {/* Card: Comparte tu enlace */}
          <section className="rounded-2xl border border-border bg-card p-6 shadow-xs">
            <div className="mb-4">
              <p className="text-sm font-semibold text-foreground">Comparte tu enlace</p>
              <p className="text-xs text-muted-foreground">
                Tu código personal: <span className="font-mono font-semibold">{code || '—'}</span>
              </p>
            </div>

            {loadingLink ? (
              <Skeleton className="h-12 w-full rounded-xl" />
            ) : (
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={fullLink}
                  className="flex-1 font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <Button className="rounded-full" onClick={handleCopy}>
                  <Copy className="mr-1 h-4 w-4" aria-hidden="true" />
                  Copiar
                </Button>
              </div>
            )}

            <Button
              className="mt-4 w-full rounded-full bg-emerald-500 text-white hover:bg-emerald-500/90"
              onClick={handleWhatsApp}
              disabled={!fullLink}
            >
              <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
              Invitar por WhatsApp
            </Button>

            <div className="mt-4 space-y-2">
              <Label htmlFor="invite-email" className="text-sm">
                Invitar por email
              </Label>
              <div className="flex gap-2">
                <Input
                  id="invite-email"
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="amigo@email.com"
                  className="flex-1"
                />
                <Button variant="outline" className="rounded-full" onClick={handleEmailInvite}>
                  <Mail className="mr-1 h-4 w-4" aria-hidden="true" />
                  Enviar
                </Button>
              </div>
            </div>

            <p className="mt-4 text-xs text-muted-foreground underline-offset-2 hover:underline cursor-pointer">
              Ver bases y condiciones del programa de referidos
            </p>
          </section>

          {/* Mis referidos */}
          <section className="rounded-2xl border border-border bg-card p-6 shadow-xs">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Mis referidos
                </p>
                <p className="text-xs text-muted-foreground">
                  Total: {seller?.total_referrals ?? referrals.length}
                </p>
              </div>
              <Users
                className="h-5 w-5 text-muted-foreground"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </div>

            {loadingRefs ? (
              <Skeleton className="h-32 w-full rounded-xl" />
            ) : referrals.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                Aún no tienes referidos. Comparte tu enlace para empezar a ganar.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {referrals.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        Referido #{r.referred_user_id.slice(0, 8)}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDate(r.created_at)}</p>
                    </div>
                    <StatusBadge variant={r.subscription_id ? 'active' : 'pending'}>
                      {r.subscription_id ? 'Activo' : 'Pendiente'}
                    </StatusBadge>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Cuotas ganadas */}
          <section className="rounded-2xl border border-border bg-card p-6 shadow-xs">
            <p className="mb-4 text-sm font-semibold text-foreground">
              Cuotas ganadas por referir
            </p>
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              No hay cuotas ganadas aún. Las cuotas gratis aparecerán acá cuando tus referidos
              completen su primera suscripción.
            </p>
          </section>
        </div>

        {/* Columna derecha (sticky info) */}
        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <Link
            to="/chat"
            className="inline-flex items-center gap-2 text-sm font-medium text-sidebar-primary hover:underline"
          >
            <Headphones className="h-4 w-4" aria-hidden="true" />
            ¿Necesitas ayuda?
          </Link>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-xs">
            <p className="mb-3 text-sm font-semibold text-foreground">
              ¿Cómo funciona el programa?
            </p>

            <div className={cn('rounded-xl bg-sidebar-accent/40 p-4')}>
              <div className="mb-1 flex items-center gap-2">
                <Gift
                  className="h-4 w-4 text-sidebar-primary"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                <p className="text-sm font-semibold text-foreground">Lo que ganas tú</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Por cada amigo que contrate un plan, ganas una <strong>cuota gratis</strong> en tu
                próxima facturación.
              </p>
            </div>

            <div className="mt-3 rounded-xl bg-primary/10 p-4">
              <div className="mb-1 flex items-center gap-2">
                <Share2 className="h-4 w-4 text-primary" strokeWidth={1.75} aria-hidden="true" />
                <p className="text-sm font-semibold text-foreground">Lo que ganan tus amigos</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Reciben <strong>15% de descuento</strong> en su primera cuota al usar tu código.
              </p>
            </div>

            <div className="mt-3 rounded-xl bg-chart-4/15 p-4">
              <p className="mb-1 text-sm font-semibold text-foreground">¡Importante!</p>
              <p className="text-xs text-foreground/80">
                Las cuotas ganadas se aplican <strong>una vez que el referido pague su primera
                cuota</strong>. El descuento del amigo es válido para nuevos socios.
              </p>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
